import { css } from "@emotion/css";
import {
  Box,
  CircularProgress,
  Typography,
  alpha,
  createTheme,
} from "@mui/material";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ReactComponent as EmptyProjectList } from "../../assets/images/ralvie/noProjects.svg";
import { ReactComponent as EmptyProjectListDark } from "../../assets/images/ralvie/noProjectsDark.svg";
import CustomButton from "../../components/CustomButton";
import RestrictPermissionPopup from "../../components/subscription-component/RestrictPermissionPopup";
import AppContext from "../../config/AppContext";
import { findProjectsByAssignedMember } from "../../services/ProjectService";
import { tagList } from "../../services/TagService";
import CommonUtil from "../../util/CommonUtils";
import { TimesheetContext, sidebarContext } from "../../util/context";
import AITimesheet from "./AITimesheet";
import UpgradePopup from "../../components/subscription-component/UpgradePopup";
import { callRefreshToken } from "../../config/http-common";

function collectNestedTitles(events) {
  let titles = [];
  function collectTitles(eventList) {
    eventList.forEach((event) => {
      if (event.events && event.events.length > 0) {
        event.events.forEach((nestedEvent) => {
          let application_name = nestedEvent?.application?.name;
          application_name = CommonUtil.isEmptyString(application_name)
            ? nestedEvent?.data?.app
            : application_name;
          let event = {
            logged_duration: String(nestedEvent.duration),
            application_names: application_name,
            title: nestedEvent.title,
          };
          titles.push(event);
        });
        collectTitles(event.events); // Recursively collect titles from deeper nested events
      }
    });
  }

  collectTitles(events); // Start collecting from the top-level events

  return titles;
}

export default function AITimesheetPredictions(props) {
  const { mode } = useContext(AppContext);
  const {
    setEventsToLog,
    isAILoading,
    setIsAILoading,
    eventsToLog,
    subEventSelected,
    SetSubEventSelected,
  } = useContext(TimesheetContext);
  const { enableAi, userProfile, setEnableAiPredictions } =
    useContext(sidebarContext);
  const [projects, setProjects] = useState([]);
  const [eventToEdit, setEventToEdit] = useState(null);
  const [tags, setTags] = useState([]);
  const [aiPredictions, setAiPredictions] = useState([]);
  const [title, setTitle] = useState("");
  const { t } = useTranslation();
  const navigate = useNavigate();
  const projectMap = projects?.reduce((acc, project) => {
    acc[project?.id] = project;
    return acc;
  }, {});
  const abortControllerRef = useRef(null);
  const [zeroProjectCount, setZeroProjectCount] = useState(false);
  const [restrictPopupOpen, setRestrictPopupOpen] = useState(false);
  const [upgradePopupOpen, setUpgradePopupOpen] = useState(false);
  const [restrictPopupOpen2, setRestrictPopupOpen2] = useState(false);
  const { subscriptionDetails } = useContext(sidebarContext);
  const { hasTimesheetFeature, setHasTimesheetFeature } = props;

  const getProjectColor = (color) => {
    const theme = createTheme({
      palette: {
        primary: {
          main: color,
          light: alpha(color, 0.1),
          dark: alpha(color, 0.9),
        },
      },
    });

    // Access the light and dark versions of the primary color
    const lightColor = theme.palette.primary.light;
    const darkColor = theme.palette.primary.dark;
    const contrastText = theme.palette.primary.contrastText;

    return {
      main: color,
      light: lightColor,
      dark: darkColor,
      contrastText: contrastText,
    };
  };

  function mergeEventsWithSameProject(events) {
    const mergedData = {};

    events.forEach((event) => {
      if (event.project) {
        let totalDuration = event?.events?.reduce((total, se) => {
          return total + se.duration;
        }, 0);
        if (!totalDuration) totalDuration = event.duration;
        console.log(event.id + " " + totalDuration);
        const projectCode = event.project?.id;
        if (projectCode && !mergedData[projectCode]) {
          let color = getProjectColor(event.project?.color);
          // If project code does not exist in mergedData, initialize it
          mergedData[projectCode] = {
            ...event,
            duration: totalDuration,
            tags: [event.tag],
            events: event.events,
            light: color.light,
            dark: color.dark,
          };
        } else {
          // If project code exists, update start and end times, sum the duration, and merge tags and events
          const existingData = mergedData[projectCode];
          existingData.start = new Date(
            Math.min(new Date(existingData.start), new Date(event.start))
          ).toISOString();
          existingData.end = new Date(
            Math.max(new Date(existingData.end), new Date(event.end))
          ).toISOString();
          existingData.duration += totalDuration;
          if (!existingData.tags.includes(event.tag)) {
            existingData.tags.push(event.tag);
          }
          if (existingData.events) {
            let subEvents = [...existingData.events, ...event.events];
            // subEvents.push(...event.events)
            existingData.events = subEvents;
          }
        }
      }
    });

    Object.values(mergedData).forEach((data) => {
      const { hr, min, sec } = CommonUtil.getDurationWithoutRoundoff(
        data.duration
      );
      data.hrs = hr;
      data.mins = min;
      data.secs = sec;
    });
    setIsAILoading(false);

    return Object.values(mergedData);
  }

  const setDefaultProject = () => {
    let project = projects[0];
    let projectEvents = [];
    let duration = 0;
    let groupedEventsByProject = [];
    props?.eventsToLog?.forEach((event) => {
      if (event.events && event.events.length > 0) {
        event.events.forEach((nestedEvent) => {
          duration = duration + nestedEvent.duration;
          projectEvents.push(nestedEvent);
        });
      }
    });
    const timeSpent = CommonUtil.getDurationWithoutRoundoff(duration);
    const data = {
      ...projectEvents[0],
      events: projectEvents,
      hrs: timeSpent?.hr,
      mins: timeSpent?.min,
      secs: timeSpent?.sec,
      project: project, // Use projectMap for faster lookup
      tagId: "",
      duraton: duration,
    };
    groupedEventsByProject.push(data);
    setAiPredictions(groupedEventsByProject);
    setIsAILoading(false);
  };

  const callProjectPredict = (events) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort(); // Cancel the previous request
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    if (
      hasTimesheetFeature &&
      events &&
      events instanceof Array &&
      projects instanceof Array &&
      projects.length > 0
    ) {
      setIsAILoading(true);
      let titles = collectNestedTitles(events);
      let groupedEventsByProject = [];

      const fetchData = async () => {
        try {
          const value = await callRefreshToken();
          if (value == true) {
            const response = await fetch(
              process.env.REACT_APP_SERVER_URL?.replace("/web", "/api/v1") +
              "/ai/project_predict",
              {
                method: "POST",
                headers: {
                  "Content-type": "application/json",
                  Authorization: localStorage.getItem("token"),
                  "accept-language": localStorage.getItem("i18nextLng"),
                  timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
                },
                body: JSON.stringify({
                  member_id: userProfile?.ralvieId,
                  events: titles,
                }),
                signal: controller.signal,
              }
            );
            const res = await response.json();
            if (
              res?.projects &&
              res?.projects instanceof Array &&
              res?.projects?.length > 0
            ) {
              let projectPredictions = res?.projects;
              let mappedEvents = new Set();

              projectPredictions?.forEach((projectPrediction) => {
                let project = projectMap[projectPrediction.project_id];
                if (!project || project?.status === "ARCHIVED") {
                  project = projects?.length > 0 ? projects[0] : null;
                }
                let projectTitles = projectPrediction.titles;
                let projectEvents = [];
                let duration = 0;

                events.forEach((event) => {
                  if (event.events && event.events.length > 0) {
                    event.events.forEach((nestedEvent) => {

                      if (!mappedEvents.has(nestedEvent?.id)) {
                        const isMatch = projectTitles.find((titles) =>
                          titles.includes(nestedEvent?.title)
                        );
                        if (
                          isMatch &&
                          subEventSelected?.includes(nestedEvent?.id)
                        ) {
                          duration = duration + nestedEvent.duration;
                          projectEvents.push(nestedEvent);
                          mappedEvents.add(nestedEvent?.id);
                        }
                      };

                    });
                  }
                });
                const timeSpent = CommonUtil.getDurationWithoutRoundoff(duration);
                const data = {
                  ...projectEvents[0],
                  events: projectEvents,
                  hrs: timeSpent?.hr,
                  mins: timeSpent?.min,
                  secs: timeSpent?.sec,
                  project: project, // Use projectMap for faster lookup
                  tagId: "",
                  tags: projectPrediction?.tags,
                  taskId: projectPrediction?.task_id,
                  duraton: duration,
                };
                if (projectEvents.length > 0) groupedEventsByProject.push(data);
              });
              if (
                mappedEvents.size > 0 &&
                groupedEventsByProject.length > 0
              ) {
                const nonMappedEvents = [];
                events.forEach((event) => {
                  if (event.events && event.events.length > 0) {
                    event.events.forEach((nestedEvent) => {
                      if (!mappedEvents.has(nestedEvent?.id)) {
                        nonMappedEvents.push(nestedEvent);
                      }
                    })
                  }
                })
                
                let projectData = groupedEventsByProject[0];
                let updatedEvents = [...projectData.events, ...nonMappedEvents];
                const timeSpent = CommonUtil.getDurationWithoutRoundoff(
                  updatedEvents.reduce((sum, event) => sum + event.duration, 0)
                );
                projectData.hrs = timeSpent?.hr;
                projectData.mins = timeSpent?.min;
                projectData.secs = timeSpent?.sec;
                projectData.events = updatedEvents;
              }
              setAiPredictions(groupedEventsByProject);
              setIsAILoading(false);
            } else {
              setIsAILoading(false);
              // setRestrictPopupOpen(true);
              // setEnableAiPredictions(false);
              setHasTimesheetFeature(false);
              // setDefaultProject();
            }
            const concatenatedTitle = [
              ...new Set(
                events.map((event) => {
                  let title = event.title || ""; // Handle case where title might be undefined
                  // Extract the portion of the title before '-' or '|'
                  const truncatedTitle = title.split(/[-|]/)[0].trim();
                  return truncatedTitle;
                })
              ),
            ].join(" • "); // Remove duplicates and join with a big dot and space
            setTitle(concatenatedTitle);
          }

        } catch (error) {
          if (error.name === "AbortError") {
            console.log("Previous API call aborted.");
          } else {
            console.error("Error:", error);
          }
        }
      };

      fetchData();

      return () => {
        controller.abort(); // Cleanup to prevent memory leaks
      };

      // setIsAILoading(true);
      // let titles = collectNestedTitles(events);
      // let groupedEventsByProject = [];

      // projectPredict({ member_id: userProfile?.ralvieId, events: titles })
      //   .then((res) => {
      //     if (
      //       res.data?.projects &&
      //       res.data?.projects instanceof Array &&
      //       res.data?.projects?.length > 0
      //     ) {
      //       let projectPredictions = res.data?.projects;
      //       projectPredictions?.forEach((projectPrediction) => {
      //         let project = projectMap[projectPrediction.project_id];
      //         if (!project || project?.status === "ARCHIVED") {
      //           project = projects?.length > 0 ? projects[0] : null;
      //         }
      //         let projectTitles = projectPrediction.titles;
      //         let projectEvents = [];
      //         let duration = 0;
      //         events.forEach((event) => {
      //           if (event.events && event.events.length > 0) {
      //             event.events.forEach((nestedEvent) => {
      //               const isMatch = projectTitles.find((titles) =>
      //                 titles.includes(nestedEvent?.title)
      //               );
      //               if (isMatch) {
      //                 duration = duration + nestedEvent.duration;
      //                 projectEvents.push(nestedEvent);
      //               }
      //             });
      //           }
      //         });
      //         const timeSpent = CommonUtil.getDurationWithoutRoundoff(duration);
      //         const data = {
      //           ...projectEvents[0],
      //           events: projectEvents,
      //           hrs: timeSpent?.hr,
      //           mins: timeSpent?.min,
      //           secs: timeSpent?.sec,
      //           project: project, // Use projectMap for faster lookup
      //           tagId: "",
      //           tags: projectPrediction?.tags,
      //           duraton: duration,
      //         };
      //         groupedEventsByProject.push(data);
      //       });
      //       setAiPredictions(groupedEventsByProject);
      //       setIsAILoading(false);
      //     } else {
      //       setDefaultProject();
      //     }
      //   })
      //   .catch((e) => {
      //     setDefaultProject();
      //     setIsAILoading(false);
      //   });

      // Extract titles before '-' or '|', remove duplicates, and concatenate with a big dot (•)
      // const concatenatedTitle = [
      //   ...new Set(
      //     events.map((event) => {
      //       let title = event.title || ""; // Handle case where title might be undefined
      //       // Extract the portion of the title before '-' or '|'
      //       const truncatedTitle = title.split(/[-|]/)[0].trim();
      //       return truncatedTitle;
      //     })
      //   ),
      // ].join(" • "); // Remove duplicates and join with a big dot and space
      // setTitle(concatenatedTitle);
    }
  };

  useEffect(() => {
    callProjectPredict(props.eventsToLog);
  }, [hasTimesheetFeature, props.eventsToLog, projects]);

  useEffect(() => {
    setZeroProjectCount(false);
    findProjectsByAssignedMember(CommonUtil.getLoggedUser()?.id)
      .then((res) => {
        let projects = res.data.data;
        if (projects instanceof Array && projects?.length > 0) {
          projects = projects?.filter((p) => p.status !== "ARCHIVED");
          if (projects?.length > 0) {
            setProjects(projects);
          } else setZeroProjectCount(true);
        } else {
          setZeroProjectCount(true);
        }
      })
      .catch((error) => {
        console.error("Error fetching projects:", error);
      });
    tagList({
      pageNumber: 1,
      pageSize: 1000,
    }).then((res) => {
      if (res.data.code === "RCI0000") {
        setTags(res.data.data?.tags);
      }
    });
  }, []);

  const handleRemoveEvent = (event) => {
    let updatedList = aiPredictions.filter((e) => e.id !== event.id);
    setAiPredictions(updatedList);
    if (updatedList.length < 1) {
      setEventsToLog([]);
    }
  };
  let role = CommonUtil.getLoggedUser()?.roleCode;
  const handleAddNewProject = () => {
    const projectCountFeature = subscriptionDetails?.features?.find(
      ({ feature }) => feature?.code === "PROJECT_COUNT"
    );

    if (isProjectLimitExceeded()) {
      if (!["OWNER"].includes(CommonUtil.getRoleCode())) {
        setRestrictPopupOpen2(true);
      } else {
        setUpgradePopupOpen(true);
      }
    } else if (!subscriptionDetails || Object.keys(subscriptionDetails).length === 0) {
      if (!["OWNER"].includes(CommonUtil.getRoleCode())) {
        setRestrictPopupOpen2(true);
      } else {
        setUpgradePopupOpen(true);
      }
    } else if (!projectCountFeature || projectCountFeature.value === undefined || projectCountFeature.value === null) {
      if (!["OWNER"].includes(CommonUtil.getRoleCode())) {
        setRestrictPopupOpen2(true);
      } else {
        setUpgradePopupOpen(true);
      }
    }
    else {
      navigate("/projects/add", {
        state: { backto: window.location.pathname + "" },
      });
    }
  };
  const isProjectLimitExceeded = () => {

    if (!subscriptionDetails) return false;
    const isActive = ["live", "non_renewing"].includes(
      subscriptionDetails.status
    );
    const projectCountLimit = subscriptionDetails?.features?.find(
      ({ feature }) => feature?.code === "PROJECT_COUNT"
    )?.value;
    const currentProjectCount = projects?.length || 0;
    const limitExceeded =
      currentProjectCount >= Number(projectCountLimit) || false;

    if (!isActive) {
      if (!["OWNER"].includes(CommonUtil.getRoleCode())) {
        setRestrictPopupOpen2(true);
        return true;
      } else {
        setUpgradePopupOpen(true);
        return true;
      }
    }

    if (limitExceeded) {
      if (!["OWNER"].includes(CommonUtil.getRoleCode())) {
        setRestrictPopupOpen2(true);
      } else {
        setUpgradePopupOpen(true);
      }
    }
    return limitExceeded;
  };
  const getPopupTitle = (subscriptionDetails, t, isRestrictPopup = false) => {
    if (CommonUtil.getStatusCondition(subscriptionDetails)) {
      return isRestrictPopup ? t("RAL0902") : t("RAL0853");
    } else if (CommonUtil.isPlanCodeDefault(subscriptionDetails)) {
      return t("RAL0850");
    } else {
      return t("RAL0852");
    }
  };

  const getPopupContent = (subscriptionDetails, t, isRestrictPopup = false) => {
    if (CommonUtil.getStatusCondition(subscriptionDetails)) {
      return isRestrictPopup ? t("RAL0903") : t("RAL0854");
    } else if (CommonUtil.isPlanCodeDefault(subscriptionDetails)) {
      return t("RAL0849");
    } else {
      return t("RAL0851");
    }
  };
  return (
    <div
      className={css`
        height: calc(100% - 40px);
        position: relative;
      `}
    >
      <>
        <Box
          item
          className={`${css`
            padding-bottom: 20px;
            padding-top: 14px;
            align-items: center;
            display: flex;
          `} header-sticky`}
        >
          <Box flexGrow={1}>
            <Typography
              variant="header3"
              sx={{
                color: "var( --text-secondary-80)",
                font: "normal normal bold 18px/20px Gotham",
                letterSpacing: "0px",
              }}
            >
              {t("RAL0623")}
            </Typography>
          </Box>
          {/* <IconButton
            aria-label="clear input"
            onClick={() => setEventsToLog([])}
            sx={{
              borderRadius: "5px",
              background: "transparent",
              "& .clear_svg": {
                transition: "color 0.3s ease",
                color: mode === "dark" ? "var(--color-33-50)" : "#242424cc",
              },
              "&:hover": {
                "& .hover-text, & .clear_svg": {
                  color: mode === "dark" ? "#f8f8f8cc" : "#242424",
                },
              },
            }}
          >
            <Tooltip title={"Clear"}>
              {mode === "dark" ? (
                <ClearIconDark className="clear_svg" />
              ) : (
                <ClearIcon className="clear_svg" />
              )}
            </Tooltip>
          </IconButton> */}
        </Box>
      </>
      {!zeroProjectCount ? (
        <div
          className={css`
            height: 70vh;
            overflow: hidden;
            :hover {
              overflow-y: auto;
            }
          `}
        >
          {aiPredictions?.map((event) => {
            return (
              <Box mb={2}>
                <AITimesheet
                  edit={aiPredictions[0]?.id}
                  title={title}
                  event={event}
                  filter={props.filter}
                  eventToEdit={eventToEdit}
                  setEventToEdit={(id) => setEventToEdit(id)}
                  handleRemoveEvent={(event) => handleRemoveEvent(event)}
                  setIsLoading={(state) => setIsAILoading(state)}
                />
              </Box>
            );
          })}
        </div>
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              flexDirection: "column",
            }}
          >
            <div>
              {mode == "dark" ? (
                <EmptyProjectListDark
                  style={{ width: "60px", height: "70px" }}
                />
              ) : (
                <EmptyProjectList style={{ width: "60px", height: "70px" }} />
              )}
            </div>
            <Typography className="cardtile-typography111">
              {t("RAL0732")}
            </Typography>
            {CommonUtil.isOwnerOrAdminOrManager() ? (
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                }}
              >
                <Typography
                  className="cardtile-typography11"
                  width={"90%"}
                  textAlign={"center"}
                >
                  {t("RAL0823")}
                </Typography>
                <Box
                  sx={{
                    height: { xl: "50px", xs: "40px" },
                    width: { xl: "150px", xs: "130px" },
                    alignSelf: "center",
                  }}
                >
                  <CustomButton
                    id="btn-add-event"
                    variant="contained"
                    fullWidth={true}
                    onClick={handleAddNewProject}
                  >
                    {t("RAL0361")}
                    {/* {"+"} */}
                  </CustomButton>
                </Box>
              </div>
            ) : (
              <Typography className="cardtile-typography">
                {t("RAL0822")}
              </Typography>
            )}
          </Box>
        </>
      )}
      <Box
        sx={{
          display: isAILoading ? "flex" : "none",
        }}
      >
        <CircularProgress
          style={{ top: "50%", left: "50%", position: "absolute", zIndex: 999 }}
        />
      </Box>
      {restrictPopupOpen && <RestrictPermissionPopup
        open={restrictPopupOpen}
        onClose={() => {
          setRestrictPopupOpen(false);
          setEventsToLog([]);
          SetSubEventSelected([]);
        }}
        title={"Hey there!"}
        content={t("RAL0957")}
        cancelText={t("RAL0568")}
      />}
      {["OWNER"].includes(CommonUtil.getRoleCode()) && (
        <UpgradePopup
          open={upgradePopupOpen}
          onClose={() => setUpgradePopupOpen(false)}
          onConfirm={() => {
            if (CommonUtil.getRoleCode() === "OWNER") {
              navigate("/subscription-manage");
            } else {
              navigate("/page-not-found");
            }
          }}
          title={getPopupTitle(subscriptionDetails, t)}
          content={getPopupContent(subscriptionDetails, t)}
          cancelText={t("RAL0177")}
          confirmText={t("RAL0848")}
        />
      )}

      {!["OWNER"].includes(CommonUtil.getRoleCode()) && (
        <RestrictPermissionPopup
          open={restrictPopupOpen2}
          onClose={() => setRestrictPopupOpen2(false)}
          title={getPopupTitle(subscriptionDetails, t, true)}
          content={getPopupContent(subscriptionDetails, t, true)}
          cancelText={t("RAL0177")}
        />
      )}
    </div>
  );
}
